import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../../../../../config/config.js";
import { useParams } from "react-router-dom";
import { formatIdSport } from "../../../../../../utils/formatIdSport.js";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import { getConfigCategory } from "../../../../../../services/configCategoryService.js";
import { showAccumulatedTab } from "../../../../../../utils/accumulatedStandingsDisplay.js";

// toast
import { toast } from "react-hot-toast";

const Acumulados = ({ acumulados, setAcumulados, getAcumulados }) => {
  const { idevent, idsport } = useParams();
  const [useAbrev, setUseAbrev] = useState(true); // Estado para alternar entre abrev y nombre
  // FTR-0065: con "Tabla acumulada (nueva)" el orden lo calcula el sistema; el intercambio manual (que dejaba las
  // métricas en la fila equivocada) no se ofrece y los empates se ordenan en Posiciones → Acumulada.
  const [accumulatedV2, setAccumulatedV2] = useState(false);

  useEffect(() => {
    let alive = true;
    if (idevent && idsport) {
      getConfigCategory({ idevent, idsport })
        .then((config) => {
          if (alive) setAccumulatedV2(showAccumulatedTab(config, idsport));
        })
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [idevent, idsport]);

  const formattedSport = formatIdSport(Number(idsport));

  // Función para obtener el subtítulo basado en el deporte
  const getSubtitulo = (formattedSport) => {
    switch (formattedSport) {
      case 1: // Fútbol
      case 2: // Futsal
        return "Solo se está tomando estos criterios: PTS > DG > GF";
      case 3: // Voleibol
        return "Solo se está tomando estos criterios: PTS > G > Ratio Set > Ratio Puntos";
      case 4: // Basquetbol
      case 5: // Handball
        return "Solo se está tomando estos criterios: PTS > DP";
      default:
        return "Criterios de desempate no especificados para este deporte.";
    }
  };

  // Función para determinar el estilo de la fila según la posición
  const getRowStyle = (index) => {
    let styles = {};

    // Aplicar un borde superior para cada grupo de seis
    if (index === 6 || index === 12 || index === 18) {
      styles.borderTop = "4px solid #cbd5e1"; // border-gray-400
    }

    // Mantener el fondo rojo para las últimas 8 filas
    if (index >= 16) {
      styles.backgroundColor = "#fecaca"; // bg-red-200
      if (index === 18) {
        styles.borderTop = "4px solid #cbd5e1"; // border-gray-400
      }
    }

    return styles;
  };

  // Función para manejar el cambio de posiciones
  const handlePositionChange = async (currentIndex, direction) => {
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    // Verificar que la nueva posición está dentro de los límites
    if (newIndex < 0 || newIndex >= acumulados.length) return;

    // Crear una copia del estado actual para actualizarlo localmente
    const updatedAcumulados = [...acumulados];

    // Intercambiar los idparticipant
    const tempParticipant = updatedAcumulados[currentIndex].idparticipant;
    updatedAcumulados[currentIndex].idparticipant =
      updatedAcumulados[newIndex].idparticipant;
    updatedAcumulados[newIndex].idparticipant = tempParticipant;

    // Actualizar el estado local para reflejar los cambios inmediatamente en la interfaz
    // setAcumulados([...updatedAcumulados]);

    // Realizar la actualización en el backend
    try {
      await axios.patch(`${API_BASE_URL}/editacumuladoparticipants`, {
        updates: [
          {
            idacumulado: updatedAcumulados[currentIndex].idacumulado,
            idparticipant: updatedAcumulados[currentIndex].idparticipant,
          },
          {
            idacumulado: updatedAcumulados[newIndex].idacumulado,
            idparticipant: updatedAcumulados[newIndex].idparticipant,
          },
        ],
      });
      getAcumulados();
      toast.success("¡Participantes actualizados exitosamente!");
    } catch (error) {
      toast.error("¡Error al intentar actualizar los participantes!");
      console.error("Error al actualizar los participantes:", error);
      // Si la actualización en el backend falla, revertir el cambio local
      setAcumulados(acumulados);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <h1 className="text-4xl font-extrabold text-center mb-6 text-gray-800">
        Tabla de Acumulados
      </h1>
      <h2 className="text-xl font-medium text-center mb-6 text-gray-600">
        {getSubtitulo(formattedSport)}
      </h2>
      {accumulatedV2 ? (
        <p className="mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          La tabla acumulada nueva está activada: el orden lo calcula el sistema y aquí no se intercambian equipos. Los
          empates perfectos se ordenan en Posiciones → Acumulada.
        </p>
      ) : null}
      <div className="flex justify-end mb-6">
        <label className="mr-2 text-gray-700 font-medium">
          Mostrar nombre completo:
        </label>
        <div className="relative inline-block w-10 mr-2 align-middle select-none">
          <input
            type="checkbox"
            name="toggle"
            id="toggle"
            checked={!useAbrev}
            onChange={() => setUseAbrev(!useAbrev)}
            className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
          />
          <label
            htmlFor="toggle"
            className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
              !useAbrev ? "bg-green-400" : "bg-gray-300"
            }`}
          ></label>
          <style jsx>{`
            .toggle-checkbox:checked {
              right: 0;
              border-color: #34d399;
            }
            .toggle-checkbox:checked + .toggle-label {
              background-color: #34d399;
            }
          `}</style>
        </div>
      </div>
      <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
        <thead>
          <tr>
            <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nro
            </th>
            <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Movimiento
            </th>
            <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Institución
            </th>
            {formattedSport === 1 ||
            formattedSport === 2 ||
            formattedSport === 5 ? (
              <>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PTS
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DG
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GF
                </th>
              </>
            ) : formattedSport === 3 ? (
              <>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PTS
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  G
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ratio Set
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ratio Puntos
                </th>
              </>
            ) : (
              <>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PTS
                </th>
                <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DP
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {acumulados.map((acum, index) => (
            <tr
              key={acum.idacumulado} // Cambiar index por idacumulado
              style={getRowStyle(index)}
              className="text-left hover:bg-gray-50 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                {acum.posicion}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                {accumulatedV2 ? (
                  <span className="text-gray-400">—</span>
                ) : (
                  <div className="flex flex-col items-center space-y-2">
                    <FaAngleUp
                      className="cursor-pointer hover:text-blue-500"
                      onClick={() => handlePositionChange(index, "up")}
                    />
                    <FaAngleDown
                      className="cursor-pointer hover:text-blue-500"
                      onClick={() => handlePositionChange(index, "down")}
                    />
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap flex items-center text-sm font-medium text-gray-900">
                <img
                  src={`${acum.participant?.Institution?.path_base}${acum.participant?.Institution?.image_path}`}
                  alt={acum.participant?.Institution?.abrev}
                  className="h-8 w-auto mr-3"
                />
                <span className="text-left">
                  {useAbrev
                    ? acum.participant?.Institution?.abrev
                    : acum.participant?.Institution?.name}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                {acum.pts}
              </td>
              {formattedSport === 1 ||
              formattedSport === 2 ||
              formattedSport === 5 ? (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.dp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.pf}
                  </td>
                </>
              ) : formattedSport === 3 ? (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.g}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.ratioSet === "99.999" ? "Max." : acum.ratioSet}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.ratioPuntos === "99.999" ? "Max." : acum.ratioPuntos}
                  </td>
                </>
              ) : (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {acum.dp}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Acumulados;
